import dayjs from 'dayjs'
import {
  CompanyFormData,
  SeasonFormData,
  OwnerFormData,
  FieldEmployee,
  AdminEmployee,
  Truck,
  Trailer,
  HeavyMachinery,
  FieldEmployeesFormData,
  AdminEmployeesFormData,
  TrucksFormData,
  TrailersFormData,
  HeavyMachineryFormData
} from './types'

export const BUILDER_ACCENT_COLOR = '#ff6b4a'

// Total steps including intro (0-8)
export const TOTAL_STEPS = 10

// Final step number (for showing "Complete" button instead of "Next")
export const FINAL_STEP = 8

// Maximum number of entities allowed in dynamic forms
export const MAX_ENTITIES = 100

// Revenue source options
export const REVENUE_OPTIONS = [
  { value: 'only_install', label: 'We only do install work' },
  { value: 'mostly_install', label: 'We mostly do install work, some maintenance' },
  { value: 'half_install_half_maintenance', label: 'We do half install work, half maintenance' },
  { value: 'mostly_maintenance', label: 'We mostly do maintenance work, some install' },
  { value: 'only_maintenance', label: 'We only do maintenance work' },
]

export const PAY_TYPE_OPTIONS = [
  { value: 'Hourly', label: 'Hourly' },
  { value: 'Salary', label: 'Salary' }
]

export const OWNERSHIP_TYPE_OPTIONS = [
  { value: 'Own', label: 'Own' },
  { value: 'Lease/Finance', label: 'Lease/Finance' }
]

export const HEAVY_MACHINERY_OPTIONS = [
  { value: 'Mini Excavator', label: 'Mini Excavator' },
  { value: 'Skid Steer', label: 'Skid Steer' },
  { value: 'Mini Skid Steer', label: 'Mini Skid Steer' },
  { value: 'Mower', label: 'Mower' },
  { value: 'Other', label: 'Other' }
]

export const getHeavyMachineryDefaults = (equipmentName: string): { replacementCost: number; avgUsefulLife: number; sellPrice: number } => {
  switch (equipmentName) {
    case 'Mini Excavator':
      return { replacementCost: 75000, avgUsefulLife: 10, sellPrice: 25000 }
    case 'Skid Steer':
      return { replacementCost: 65000, avgUsefulLife: 10, sellPrice: 20000 }
    case 'Mini Skid Steer':
      return { replacementCost: 45000, avgUsefulLife: 10, sellPrice: 15000 }
    case 'Mower':
      return { replacementCost: 10000, avgUsefulLife: 5, sellPrice: 2500 }
    default:
      return { replacementCost: 45000, avgUsefulLife: 10, sellPrice: 15000 }
  }
}

// Default form data
export const DEFAULT_COMPANY_DATA: CompanyFormData = {
  salesGoal: 1000000,
  profitMargin: 20,
  revenueSource: 'half_install_half_maintenance',
  seasonalSnowWork: false,
  subcontractedAmount: null
}

const currentYear = dayjs().year()

export const DEFAULT_SEASON_DATA: SeasonFormData = {
  seasonStartDate: dayjs(`${currentYear}-04-01`),
  seasonEndDate: dayjs(`${currentYear}-12-31`),
  daysPerWeek: 5,
  hoursPerWeek: 40
}

export const DEFAULT_OWNER_DATA: OwnerFormData = {
  fieldWorkPercentage: 50,
  hoursPerWeek: 40,
  annualSalary: 100000
}

// Default employee factories
export const createDefaultFieldEmployee = (index: number, hoursPerWeek: number | null = 40): FieldEmployee => ({
  name: `Employee ${index + 1}`,
  payType: 'Hourly',
  hourlyRate: 18.5,
  hoursPerWeek,
  annualSalary: null
})

export const createDefaultAdminEmployee = (index: number): AdminEmployee => ({
  name: `Employee ${index + 1}`,
  payType: 'Salary',
  annualSalary: 47958,
  hourlyRate: null,
  hoursPerWeek: null
})

export const createDefaultTruck = (index: number): Truck => ({
  name: `Truck ${index + 1}`,
  ownershipType: 'Own',
  replacementCost: 80000,
  avgUsefulLife: 7,
  sellPrice: 25000,
  monthlyPayment: null
})

export const createDefaultTrailer = (index: number): Trailer => ({
  name: `Trailer ${index + 1}`,
  ownershipType: 'Own',
  replacementCost: 10000,
  avgUsefulLife: 10,
  sellPrice: 3500,
  monthlyPayment: null
})

export const createDefaultHeavyMachinery = (_index: number): HeavyMachinery => {
  const defaults = getHeavyMachineryDefaults('Mini Excavator')
  return {
    equipmentName: 'Mini Excavator',
    customEquipmentName: undefined,
    ownershipType: 'Own',
    replacementCost: defaults.replacementCost,
    avgUsefulLife: defaults.avgUsefulLife,
    sellPrice: defaults.sellPrice,
    monthlyPayment: null
  }
}

export const DEFAULT_FIELD_EMPLOYEES_DATA: FieldEmployeesFormData = {
  employeeCount: 0,
  employees: []
}

export const DEFAULT_ADMIN_EMPLOYEES_DATA: AdminEmployeesFormData = {
  employeeCount: 0,
  employees: []
}

export const DEFAULT_TRUCKS_DATA: TrucksFormData = {
  truckCount: 0,
  trucks: []
}

export const DEFAULT_TRAILERS_DATA: TrailersFormData = {
  trailerCount: 0,
  trailers: []
}

export const DEFAULT_HEAVY_MACHINERY_DATA: HeavyMachineryFormData = {
  machineryCount: 0,
  machinery: []
}
